import type { Locale } from '../i18n/t.js';

export interface AudioServiceOptions {
  getLocale: () => Locale;
  /** When true, play() resolves immediately without producing sound. Used by E2E (`?test-mute=1`). */
  testMute?: boolean;
  /** Override the asset base. Defaults to `/audio`. */
  audioBase?: string;
  /** HTMLAudioElement factory; injectable for tests. */
  createAudio?: (src: string) => HTMLAudioElement;
}

export type AudioPriority = 'prompt' | 'reward';

interface PendingPlay {
  key: string;
  priority: AudioPriority;
  resolve: () => void;
}

/**
 * Pre-recorded audio playback per design decision 3.
 *
 * Runtime rules:
 *  - Prompt audio interrupts prompt audio.
 *  - Reward audio is uninterruptible; subsequent prompts are queued until it
 *    finishes.
 *  - Playback is gated behind `unlock()` (browser autoplay-policy compliance).
 *  - When `testMute` is true OR the app is in a non-browser environment, all
 *    `play()` calls resolve immediately without producing sound; events are
 *    still dispatched so DOM-event assertions remain meaningful.
 *  - Missing MP3 files (e.g. before generation has been run) log a warning
 *    and resolve — the rest of the app keeps working.
 */
export class AudioService extends EventTarget {
  private opts: AudioServiceOptions;
  private unlocked = false;
  private currentAudio: HTMLAudioElement | null = null;
  private currentPriority: AudioPriority | null = null;
  private queue: PendingPlay[] = [];

  constructor(opts: AudioServiceOptions) {
    super();
    this.opts = opts;
  }

  isUnlocked(): boolean {
    return this.unlocked;
  }

  unlock(): void {
    this.unlocked = true;
    this.dispatchEvent(new CustomEvent('unlock'));
  }

  async preload(keys: readonly string[]): Promise<void> {
    if (this.opts.testMute) return;
    const locale = this.opts.getLocale();
    await Promise.allSettled(keys.map((k) => this.fetchOnce(this.resolveSrc(locale, k))));
  }

  async play(key: string, priority: AudioPriority = 'prompt'): Promise<void> {
    this.dispatchEvent(new CustomEvent('audio-play', { detail: { key, priority } }));
    if (this.opts.testMute) return;
    if (!this.unlocked) return;

    return new Promise<void>((resolve) => {
      // Reward currently playing → always queue.
      if (this.currentPriority === 'reward') {
        this.queue.push({ key, priority, resolve });
        return;
      }
      // Prompt currently playing and new is prompt → interrupt and replace.
      // Prompt currently playing and new is reward → interrupt prompt, play reward immediately.
      if (this.currentPriority === 'prompt') {
        this.stop();
      }
      this.startPlay(key, priority, resolve);
    });
  }

  stop(): void {
    if (this.currentAudio) {
      this.currentAudio.pause();
      this.currentAudio.src = '';
      this.currentAudio = null;
    }
    this.currentPriority = null;
  }

  private resolveSrc(locale: Locale, key: string): string {
    const base = this.opts.audioBase ?? '/audio';
    return `${base}/${locale}/${key.replace(/\./g, '-')}.mp3`;
  }

  private fetchOnce(src: string): Promise<void> {
    return new Promise((resolve) => {
      const audio = (this.opts.createAudio ?? defaultCreateAudio)(src);
      audio.preload = 'auto';
      audio.addEventListener('canplaythrough', () => resolve(), { once: true });
      audio.addEventListener('error', () => resolve(), { once: true });
    });
  }

  private startPlay(key: string, priority: AudioPriority, resolve: () => void): void {
    const src = this.resolveSrc(this.opts.getLocale(), key);
    const audio = (this.opts.createAudio ?? defaultCreateAudio)(src);
    this.currentAudio = audio;
    this.currentPriority = priority;
    const onEnd = (): void => {
      audio.removeEventListener('ended', onEnd);
      audio.removeEventListener('error', onError);
      this.currentAudio = null;
      this.currentPriority = null;
      this.dispatchEvent(new CustomEvent('audio-ended', { detail: { key, priority } }));
      resolve();
      this.flushQueue();
    };
    const onError = (): void => {
      console.warn(`[audio] failed to play "${key}" (${src}); continuing without sound`);
      onEnd();
    };
    audio.addEventListener('ended', onEnd, { once: true });
    audio.addEventListener('error', onError, { once: true });
    audio.play().catch(onError);
  }

  private flushQueue(): void {
    const next = this.queue.shift();
    if (!next) return;
    this.startPlay(next.key, next.priority, next.resolve);
  }
}

function defaultCreateAudio(src: string): HTMLAudioElement {
  return new Audio(src);
}

export function detectTestMute(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    return new URLSearchParams(window.location.search).has('test-mute');
  } catch {
    return false;
  }
}
